/**
 * ADR-0210 Slice 5 — Overview tab: the story of the site's agent traffic.
 *
 *   tiles     the window's numbers with the change vs the prior window
 *   traffic   daily page loads stacked by the top agents (+ other)
 *   who       share of traffic by category (AI agents vs crawlers…)
 *   funnel    sessions that reached product → search → cart → checkout,
 *             then governed actions → approved; per agent underneath
 *   identity  share of traffic by how well the caller identified itself
 *   when      weekday × hour heatmap of page loads (UTC)
 *
 * Derived from daily rollups (never signed records); the view says so.
 */

import type { AgentKind, AgentOverviewFunnel, AgentOverviewResponse } from '@sill/types/api';

import { Heatmap } from '../../components/atoms/Heatmap.js';
import { HoverBars } from '../../components/atoms/HoverBars.js';
import { StackedBars, useBandColors } from '../../components/atoms/StackedBars.js';
import { Tooltip } from '../../components/atoms/Tooltip.js';
import { useC } from '../../lib/theme.js';
import { CATEGORY_LABEL, KIND_WORDS, agentDisplayName, fmtDay, fmtInt, fmtMoney, fmtPct, monoFont, sansFont } from './format.js';

const FUNNEL_STEPS: ReadonlyArray<{ key: keyof AgentOverviewFunnel; label: string; unit: 'sessions' | 'requests' }> = [
  { key: 'sessions', label: 'Sessions', unit: 'sessions' },
  { key: 'product', label: 'Viewed a product', unit: 'sessions' },
  { key: 'search', label: 'Searched', unit: 'sessions' },
  { key: 'cart', label: 'Reached cart', unit: 'sessions' },
  { key: 'checkout', label: 'Reached checkout', unit: 'sessions' },
  { key: 'actions', label: 'Requested an action', unit: 'requests' },
  { key: 'approved', label: 'Approved', unit: 'requests' },
];

// Keys match the server's `AgentOverviewIdentity` buckets (sites-agent-
// overview.ts `identityBucketOf`): one weighted-by-page-loads count per
// (agent, day), across ALL agents in the window — not just the top five.
// The old implementation re-derived counts from `funnel.by_agent`, which
// only carries the top five, so impersonators behind the top five showed
// zero. Mapping is defined once here and the renderer reads directly
// from `data.identity`.
type IdentityKey = 'known' | 'unregistered' | 'no_identity' | 'impersonation' | 'person';
const IDENTITY_ROW_META: ReadonlyArray<{ key: IdentityKey; label: string; hint: string; coloured: boolean }> = [
  { key: 'known', label: 'Known agent', hint: 'A match in Sill’s registry, or the caller presented a signature or Cloudflare-verified fingerprint Sill could check.', coloured: false },
  { key: 'unregistered', label: 'Unregistered', hint: 'It identified itself but is not in Sill’s registry and sent nothing Sill could independently check.', coloured: false },
  { key: 'no_identity', label: 'No identity string', hint: 'The caller sent no identity string, or only a bare token (no OS/browser/bot name).', coloured: false },
  { key: 'impersonation', label: 'Likely impersonation', hint: 'The caller used a known bot’s name but Sill could not verify it — probe traffic, a misspelled name, or a Cloudflare mismatch.', coloured: true },
  { key: 'person', label: 'Person', hint: 'A plain browser with no bot token — a person, or a browser-mode agent that does not identify itself.', coloured: false },
];

function Kicker({ children }: { children: string }): JSX.Element {
  const C = useC();
  return <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: C.muted, marginBottom: 10 }}>{children}</div>;
}

function Panel({ title, sub, children }: { title: string; sub?: string | undefined; children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <div className="p-3 sm:p-4" style={{ border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface, minWidth: 0 }}>
      <Kicker>{title}</Kicker>
      {sub !== undefined ? <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: -4, marginBottom: 12 }}>{sub}</div> : null}
      {children}
    </div>
  );
}

/** "+40%" / "−12%" / "new" / "—" against the prior window. */
export function deltaLabel(now: number, prior: number): { text: string; tone: 'up' | 'down' | 'flat' | 'new' } {
  if (prior === 0 && now === 0) return { text: '—', tone: 'flat' };
  if (prior === 0) return { text: 'new', tone: 'new' };
  const pct = Math.round(((now - prior) / prior) * 100);
  if (pct === 0) return { text: '±0%', tone: 'flat' };
  return { text: `${pct > 0 ? '+' : '−'}${Math.abs(pct).toLocaleString('en-US')}%`, tone: pct > 0 ? 'up' : 'down' };
}

function DeltaTile({ label, value, now, prior, invert = false, prior_label }: { label: string; value: string; now: number; prior: number; invert?: boolean; prior_label: string }): JSX.Element {
  const C = useC();
  const d = deltaLabel(now, prior);
  const good = d.tone === 'up' ? !invert : d.tone === 'down' ? invert : null;
  const color = d.tone === 'flat' ? C.dim : d.tone === 'new' ? C.skyFg : good ? C.limeFg : C.coralFg;
  return (
    <div style={{ padding: '12px 14px', border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }} data-testid={`tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: C.muted }}>{label}</div>
      <div style={{ fontFamily: monoFont, fontSize: 20, fontWeight: 600, color: C.text, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <Tooltip content={`${prior_label}: ${fmtInt(prior)}`} displayMode="inline-flex">
        <div style={{ fontFamily: monoFont, fontSize: 10, color, marginTop: 2, cursor: 'default' }}>{d.text} <span style={{ color: C.dim }}>vs prior</span></div>
      </Tooltip>
    </div>
  );
}

export interface OverviewProps {
  readonly data: AgentOverviewResponse;
  readonly kind?: AgentKind | undefined;
  readonly onOpenAgent: (agent_key: string) => void;
}

export function Overview({ data, kind = 'all', onOpenAgent }: OverviewProps): JSX.Element {
  const C = useC();
  const W = KIND_WORDS[kind];
  const palette = useBandColors();
  const { totals, prior, series, categories, funnel } = data;
  const prior_label = `${fmtDay(data.prior_window.from_day)} – ${fmtDay(data.prior_window.to_day)}`;
  const bands: Array<{ label: string; values: readonly number[]; agent_key: string | null; color?: string }> = [
    ...series.agents.map((a) => ({ label: agentDisplayName(a), values: a.traffic, agent_key: a.agent_key })),
    ...(series.other.some((v) => v > 0) ? [{ label: `Other ${W.plural}`, values: series.other, agent_key: null, color: C.dim }] : []),
  ];
  // "Who they are": under All the interesting split is by category (an
  // "agent" is often an SEO crawler); under a specific filter the category
  // is a tautology, so the split is by agent instead.
  const share: Array<[string, number]> = kind === 'all'
    ? Object.entries(categories).sort((a, b) => b[1] - a[1])
    : [
      ...series.agents.map((a) => [agentDisplayName(a), a.traffic.reduce((s, v) => s + v, 0)] as [string, number]),
      ...(series.other.some((v) => v > 0) ? [[`Other ${W.plural}`, series.other.reduce((s, v) => s + v, 0)] as [string, number]] : []),
    ].filter(([, n]) => n > 0);
  const share_label = (k: string): string => (kind === 'all' ? (CATEGORY_LABEL[k] ?? k) : k);
  const cat_total = share.reduce((s, [, v]) => s + v, 0);
  const cats = share;
  // Identity counts come straight from the server — one bucket per
  // (agent, day) weighted by page_loads across ALL agents in the window
  // (not just the top five). This is the single source of truth; the
  // dashboard renders the five keys the server reports plus `person`
  // (which is `identity.human`). See
  // `services/api/src/http/routes/v1/sites-agent-overview.ts` →
  // `identityBucketOf`.
  const identity_counts: Record<IdentityKey, number> = {
    known: data.identity.known,
    unregistered: data.identity.unregistered,
    no_identity: data.identity.no_identity,
    impersonation: data.identity.impersonation,
    person: data.identity.human,
  };
  const id_total = Object.values(identity_counts).reduce((s, v) => s + v, 0);
  const has_heat = data.heatmap.some((row) => row.some((v) => v > 0));
  const funnel_max = Math.max(1, funnel.sessions, funnel.actions);

  return (
    <div data-testid="overview" style={{ display: 'grid', gap: 12, minWidth: 0 }}>
      <div className={`grid grid-cols-2 md:grid-cols-3 gap-2 ${kind === 'humans' ? '' : 'lg:grid-cols-6'}`}>
        <DeltaTile label="Page loads" value={fmtInt(totals.traffic)} now={totals.traffic} prior={prior.traffic} prior_label={prior_label} />
        <DeltaTile label={W.plural} value={fmtInt(totals.agents)} now={totals.agents} prior={prior.agents} prior_label={prior_label} />
        <DeltaTile label="Sessions" value={fmtInt(totals.sessions)} now={totals.sessions} prior={prior.sessions} prior_label={prior_label} />
        {kind !== 'humans' ? (
          <>
            <DeltaTile label="Actions" value={fmtInt(totals.actions)} now={totals.actions} prior={prior.actions} prior_label={prior_label} />
            <DeltaTile label="Refused" value={fmtInt(totals.rejected)} now={totals.rejected} prior={prior.rejected} invert prior_label={prior_label} />
            <DeltaTile label="Revenue" value={fmtMoney(totals.revenue_minor, totals.currency)} now={totals.revenue_minor} prior={prior.revenue_minor} prior_label={prior_label} />
          </>
        ) : null}
      </div>

      <Panel title={`Traffic by ${W.singular} · daily page loads`} sub={`Hover a day for the split. Bands are the top five ${W.plural} in the window; everyone else is grey.`}>
        {series.days.length === 0 || bands.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim }}>No page loads from {W.plural} in this window.</div>
        ) : (
          <>
            <StackedBars days={series.days} series={bands} width={720} height={160} formatDay={fmtDay} formatValue={fmtInt} aria-label="Daily agent page loads by agent" />
            <div className="flex flex-wrap gap-x-4 gap-y-1" style={{ marginTop: 10 }}>
              {bands.map((b, i) => (
                <button
                  key={b.label}
                  type="button"
                  disabled={b.agent_key === null}
                  onClick={() => { if (b.agent_key !== null) onOpenAgent(b.agent_key); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', padding: 0, cursor: b.agent_key === null ? 'default' : 'pointer', fontFamily: sansFont, fontSize: 12, color: C.text }}
                >
                  <span style={{ width: 9, height: 9, borderRadius: 1, background: b.color ?? palette[i % palette.length] }} />
                  {b.label}
                  <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>{fmtInt(b.values.reduce((s, v) => s + v, 0))}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </Panel>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Panel title={`${W.who} · share of page loads`} sub={kind === 'all' ? 'An “agent” is often not an AI assistant. This is the split by what each caller is.' : `The ${W.plural} behind this window's page loads.`}>
          {cats.length === 0 ? <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim }}>No {W.plural} loaded a page in this window.</div> : (
            <>
              <div style={{ display: 'flex', height: 10, borderRadius: 2, overflow: 'hidden', background: C.surface2 }} data-testid="category-bar">
                {cats.map(([k, v], i) => (
                  <Tooltip key={k} content={`${share_label(k)}: ${fmtInt(v)} · ${fmtPct(v, cat_total)}`} displayMode="block" style={{ width: `${(v / cat_total) * 100}%` }}>
                    <div style={{ height: 10, background: palette[i % palette.length], opacity: 0.85 }} />
                  </Tooltip>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 12 }}>
                {cats.map(([k, v], i) => (
                  <div key={k} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 10, alignItems: 'center', fontFamily: sansFont, fontSize: 12.5, color: C.text }}>
                    <span style={{ width: 9, height: 9, borderRadius: 1, background: palette[i % palette.length] }} />
                    <span>{share_label(k)}</span>
                    <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{fmtPct(v, cat_total)}</span>
                    <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, fontVariantNumeric: 'tabular-nums', minWidth: 48, textAlign: 'right' }}>{fmtInt(v)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Panel>

        <Panel title={`Funnel · how far ${W.plural} get`} sub="Browser sessions that reached each page, then governed requests. Server-side fetchers (no JavaScript) show as traffic but cannot have sessions. Hover a step for its share.">
          {funnel.sessions === 0 && funnel.actions === 0 ? (
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim }}>
              {kind === 'bots'
                ? 'Crawlers and tools fetch pages server-side without running the Sill script, so there are no browser sessions to follow and no governed requests.'
                : kind === 'humans'
                  ? 'No human browser sessions in this window yet — they are kept from 2026-09-23 forward.'
                  : `No browser sessions or governed requests from ${W.plural} in this window. Sessions come from page loads that ran the Sill script; server-side fetchers show as traffic only.`}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="funnel">
              {FUNNEL_STEPS.filter((step) => kind !== 'humans' || step.unit === 'sessions').map((step) => {
                const v = funnel[step.key];
                const base = step.unit === 'sessions' ? funnel.sessions : funnel.actions;
                return (
                  <Tooltip key={step.key} content={`${fmtInt(v)} ${step.unit} · ${fmtPct(v, base)} of ${step.unit === 'sessions' ? 'sessions' : 'requests'}`} displayMode="block">
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(96px, 150px) 1fr auto', gap: 10, alignItems: 'center', cursor: 'default' }}>
                      <span style={{ fontFamily: sansFont, fontSize: 12.5, color: step.unit === 'requests' ? C.limeFg : C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{step.label}</span>
                      <div style={{ height: 14, background: C.surface2, borderRadius: 2 }}>
                        <div style={{ height: 14, width: `${Math.max(v > 0 ? 1 : 0, Math.round((v / funnel_max) * 100))}%`, background: step.unit === 'requests' ? C.limeFg : C.skyFg, opacity: 0.85, borderRadius: 2 }} />
                      </div>
                      <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, fontVariantNumeric: 'tabular-nums', minWidth: 40, textAlign: 'right' }}>{fmtInt(v)}</span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {kind !== 'humans' ? (
        <Panel title="Identity · can you trust who they say they are" sub={`Share of ${W.plural}' page loads by how well the caller identified itself. Only Sill sees both the claim and the verification.`}>
          {id_total === 0 ? <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim }}>No {W.plural} in this window.</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="identity">
              {IDENTITY_ROW_META.filter((row) => row.key !== 'person' || kind === 'all').map((row) => {
                const v = identity_counts[row.key];
                // One bar colour per row-tone: coral for the "likely
                // impersonation" row (the only one that reads as a
                // warning), the muted band colour everywhere else so
                // the rows read as one chart, not five.
                const bar = row.coloured ? C.coralFg : C.skyFg;
                return (
                  <Tooltip key={row.key} content={row.hint} displayMode="block">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'center', cursor: 'default' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: sansFont, fontSize: 12.5, color: v > 0 ? C.text : C.muted, lineHeight: 1.3 }}>{row.label}</div>
                        <div style={{ height: 4, background: C.surface2, borderRadius: 2, marginTop: 4 }}>
                          <div style={{ height: 4, width: `${Math.max(v > 0 ? 1 : 0, Math.round((v / id_total) * 100))}%`, background: bar, borderRadius: 2 }} />
                        </div>
                      </div>
                      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{fmtPct(v, id_total)}</span>
                      <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, fontVariantNumeric: 'tabular-nums', minWidth: 48, textAlign: 'right' }}>{fmtInt(v)}</span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </Panel>
        ) : null}

        <Panel title={`When ${W.plural} come · weekday × hour (UTC)`} sub="Hover or tap a cell for the count.">
          {has_heat ? <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 300 }}><Heatmap rows={data.heatmap} formatValue={fmtInt} /></div></div> : <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim }}>No page hits from {W.plural} in this window. Hours count page loads (browser and server-side); signed-record-only traffic has no hour.</div>}
        </Panel>
      </div>

      {funnel.by_agent.length > 0 ? (
        <Panel title={`Funnel by ${W.singular} · top five`}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }} data-testid="funnel-by-agent">
              <thead>
                <tr style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px 8px 0', fontWeight: 500 }}>Agent</th>
                  {FUNNEL_STEPS.map((s) => <th key={s.key} style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500, whiteSpace: 'nowrap' }}>{s.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {funnel.by_agent.map((a) => (
                  <tr key={a.agent_key} style={{ borderTop: `1px solid ${C.borderSoft}`, cursor: 'pointer' }} onClick={() => onOpenAgent(a.agent_key)} data-agent-key={a.agent_key}>
                    <td style={{ padding: '8px 8px 8px 0', color: C.text, fontWeight: 500, whiteSpace: 'nowrap' }}>{agentDisplayName(a)}</td>
                    {FUNNEL_STEPS.map((s) => {
                      const v = a[s.key];
                      const base = s.unit === 'sessions' ? a.sessions : a.actions;
                      return (
                        <td key={s.key} style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: v > 0 ? C.text : C.muted, whiteSpace: 'nowrap' }}>
                          {fmtInt(v)}{s.key !== 'sessions' && v > 0 ? <span style={{ fontSize: 10, color: C.muted, marginLeft: 5 }}>{fmtPct(v, base)}</span> : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.dim, textTransform: 'uppercase' }}>
        Derived from daily rollups · not signed records · prior window {prior_label}
      </div>
    </div>
  );
}

/** Refusals per day by reason — rendered in the Demand tab. */
export function RefusalsByDay({ rows }: { rows: AgentOverviewResponse['refusals_by_day'] }): JSX.Element | null {
  const C = useC();
  if (rows.length === 0) return null;
  const bars = rows.map((r) => {
    const entries = Object.entries(r.reasons).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((s, [, n]) => s + n, 0);
    return { value: total, tooltip: [fmtDay(r.day), `${fmtInt(total)} refused`, ...entries.slice(0, 4).map(([k, n]) => `${k.replace(/_/g, ' ')} · ${fmtInt(n)}`)] };
  });
  const first = rows[0]!.day;
  const last = rows[rows.length - 1]!.day;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.dim, marginBottom: 6 }}>REFUSALS BY DAY · HOVER FOR REASONS</div>
      <HoverBars fluid width={640} height={70} bars={bars} axis={{ first: fmtDay(first), last: fmtDay(last) }} aria-label="Refused agent requests per day" />
    </div>
  );
}
